<template>
  <div
    ref="containerEl"
    class="flowbuilder-node-container"
  >
    <div
      :class="{
        'flowbuilder-tests': hasTest,
        'flowbuilder-tests-expanded': hasTest
      }"
    >
      <div
        v-if="hasTest"
        class="flowbuilder-tests-header"
      >
        <span>Question Split Test</span>
        <div
          class="flowbuilder-tests-status"
          :class="testStatusClasses"
        >
          <div class="flowbuilder-tests-status-icon">
            <component
              :is="testStatusIcon"
              class="h-4 w-4"
            />
          </div>
          <div class="flowbuilder-tests-status-label">
            {{ testStatusLabel }}
            <a
              v-if="testIsActive"
              class="flowbuilder-tests-view-results"
              @click.stop="handleViewResults"
            >
              View Results
            </a>
            <a
              v-if="testIsDraft"
              class="flowbuilder-tests-view-results"
              @click.stop="handleStartTest"
            >
              Start test
            </a>
            <span
              v-if="testIsDraft"
              class="mx-1"
            >-</span>
            <a
              v-if="testIsDraft"
              class="flowbuilder-tests-view-results"
              @click.stop="handleCancelTest"
            >
              Cancel test
            </a>
          </div>
        </div>
      </div>
      <div class="flowbuilder-tests-content">
        <BaseNode
          :node="node"
          :icon="nodeIcon"
          :classes="nodeBackground"
          :metrics="sanitizedMetrics"
          :menu-items="question?.inputType ? [] : menuItems"
          :delete-label="'Remove asking this question'"
          :context="hasTest ? 'CONTROL' : null"
          @delete-node="handleDelete"
        >
          <!-- Node type label -->
          <template #context>
            {{ contextText }}
          </template>

          <!-- Settings menu slot for webapp-specific actions -->
          <template #settings-menu>
            <slot name="settings-menu" />
          </template>

          <!-- Main content area -->
          <template v-if="node.ask">
            <template v-if="question">
              <div class="mb-1 mt-3 text-xs opacity-60">
                {{
                  segmentGroup?.name ||
                    (question.inputType ? inputLabel : 'Question')
                }}
              </div>
              <div class="">
                "{{ questionText }}"
              </div>
              <div
                v-if="hasTest && segmentGroup"
                class="-mx-2 my-2"
              >
                <!-- Bar chart visualization of answer options -->
                <div class="space-y-2">
                  <template
                    v-for="segment in sortedSegments"
                    :key="segment.id"
                  >
                    <div
                      class="segment-bar-inline relative"
                      :style="{
                        '--segment-width': audienceBreakdown?.total
                          ? `${((segment.count / audienceBreakdown.total) * 100).toFixed(0)}%`
                          : '0%'
                      }"
                    >
                      <div
                        class="flex justify-between gap-x-6 px-3 py-1 text-sm"
                      >
                        <div class="flex items-start leading-4">
                          <span class="mr-2">•</span>
                          <span>{{ getSegmentName(segment.id) }}</span>
                        </div>
                        <div class="flex space-x-1">
                          <div>
                            {{ new Intl.NumberFormat().format(segment.count) }}
                          </div>
                          <div class="opacity-30">
                            /
                          </div>
                          <div class="font-bold">
                            {{
                              audienceBreakdown?.total
                                ? (
                                  (segment.count / audienceBreakdown.total) *
                                  100
                                ).toFixed(1)
                                : '0.0'
                            }}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </template>
            <template v-else>
              Select a question
            </template>

            <!-- Sync status indicators -->
            <div
              v-if="!templatePreview"
              class="mt-2.5 flex items-center gap-x-1"
            >
              <div
                v-if="showSyncStatus && segmentGroup && syncStatus"
                class="inline-block"
              >
                <div
                  class="flex items-center gap-x-1 rounded px-1.5 py-0.5"
                  :class="syncStatusClasses"
                  :title="syncStatusTitle"
                >
                  <div class="text-xs">
                    {{ syncStatusText }}
                  </div>
                  <component
                    :is="loopIcon"
                    class="h-4 w-4"
                  />
                </div>
              </div>
              <div
                v-if="
                  showSyncStatus && question?.inputType && question?.syncToField
                "
                class="inline-block"
              >
                <div
                  class="flex items-center gap-x-1 rounded bg-green-500/30 px-1.5 py-0.5"
                  title="Input synced to custom field"
                >
                  <div class="text-xs">
                    Synced
                  </div>
                  <component
                    :is="loopIcon"
                    class="h-4 w-4"
                  />
                </div>
              </div>
              <div
                v-if="segmentGroup?.isMultiWinning"
                class="flex items-center gap-x-1 rounded bg-purple-500/10 px-1.5 py-0.5"
                title="Multiple answers can be selected"
              >
                <div class="text-xs">
                  Multi Select
                </div>
                <component
                  :is="multiSelectIcon"
                  class="h-4 w-4"
                />
              </div>
              <div
                v-if="question?.otherOptions?.enabled"
                class="flex items-center gap-x-1 rounded px-1.5 py-0.5"
                :class="question?.otherOptions?.syncToCustomField ? 'bg-green-500/30' : 'bg-slate-500/10'"
                :title="question?.otherOptions?.syncToCustomField ? 'Open-ended responses synced to custom field' : 'Capturing open ended responses (not synced)'"
              >
                <div class="text-xs">
                  {{ question?.otherOptions?.syncToCustomField ? 'Other synced' : 'Open ended' }}
                </div>
                <component
                  :is="question?.otherOptions?.syncToCustomField ? loopIcon : openEndedIcon"
                  class="h-4 w-4"
                />
              </div>
              <div
                v-if="questionHasScoring"
                class="flex items-center gap-x-1 rounded bg-emerald-500/10 px-1.5 py-0.5"
                title="Answer options have scoring"
              >
                <div class="text-xs">
                  Scoring enabled
                </div>
                <component
                  :is="scoreIcon"
                  class="h-4 w-4"
                />
              </div>
            </div>
          </template>
          <template v-else>
            <template v-if="segmentGroup">
              <div>{{ segmentGroup.name }}</div>
            </template>
            <template v-else-if="node.type === 'random'">
              Randomly sending visitors to one of the following
              {{ node.split?.length || 0 }} groups
            </template>
            <template v-else>
              Select a segment group / question to route by
            </template>
          </template>

          <!-- Hover tooltip with segments (with or without audience breakdown) -->
          <template
            v-if="node.ask && segmentGroup && !hasTest"
            #hover
          >
            <div
              :style="{
                display: 'flex',
                maxWidth: '300px',
                flexDirection: 'column',
                gap: '1rem',
                padding: '1rem 0.5rem'
              }"
            >
              <div
                :style="{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }"
              >
                <div
                  v-if="scale < 1"
                  class="mb-3 text-base font-bold"
                >
                  {{ questionText }}
                </div>

                <!-- With audience breakdown -->
                <template v-if="audienceBreakdown">
                  <template
                    v-for="segment in sortedSegments"
                    :key="segment.id"
                  >
                    <div :style="{ position: 'relative' }">
                      <div
                        class="segment-bar-hover"
                        :style="{
                          position: 'relative',
                          '--segment-width': audienceBreakdown.total
                            ? `${((segment.count / audienceBreakdown.total) * 100).toFixed(0)}%`
                            : '0%'
                        }"
                      >
                        <div
                          :style="{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '1.5rem',
                            padding: '0.25rem 0 0.25rem 0.5rem',
                            fontSize: '0.875rem',
                            color: 'inherit'
                          }"
                        >
                          <div
                            :style="{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              flex: 1
                            }"
                          >
                            <img
                              v-if="
                                question?.displayType === 'images' &&
                                  questionOptionForSegment(segment.id)?.imageUrl
                              "
                              :src="
                                getExternalUrl(
                                  questionOptionForSegment(segment.id)?.imageUrl
                                )
                              "
                              :style="{
                                objectFit: 'cover',
                                width: '30px',
                                height: '30px',
                                borderRadius: '0.25rem',
                                flexShrink: 0
                              }"
                            >
                            <div
                              :style="{
                                lineHeight: '1rem',
                                textAlign: 'left'
                              }"
                            >
                              <div>
                                {{ answerText(getSegmentName(segment.id)) }}
                              </div>
                              <div
                                v-if="getSegmentScore(segment.id)"
                                :style="{
                                  fontSize: '0.75rem',
                                  opacity: 0.6,
                                  marginTop: '0.125rem'
                                }"
                              >
                                {{ getSegmentScore(segment.id) }}
                              </div>
                            </div>
                          </div>
                          <div
                            v-if="!templatePreview"
                            :style="{
                              display: 'flex',
                              gap: '0.25rem'
                            }"
                          >
                            <div>
                              {{
                                new Intl.NumberFormat().format(segment.count)
                              }}
                            </div>
                            <div :style="{ opacity: 0.3 }">
                              /
                            </div>
                            <div :style="{ fontWeight: 'bold' }">
                              {{
                                audienceBreakdown.total
                                  ? (
                                    (segment.count /
                                      audienceBreakdown.total) *
                                    100
                                  ).toFixed(1)
                                  : '0.0'
                              }}%
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </template>

                <!-- Without audience breakdown - just show segment names -->
                <template v-else>
                  <template
                    v-for="segment in segmentGroup.segments"
                    :key="segment.id"
                  >
                    <div
                      :style="{
                        padding: '0.25rem 0',
                        fontSize: '0.875rem',
                        color: 'inherit'
                      }"
                    >
                      <div
                        :style="{
                          padding: '0 0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }"
                      >
                        <img
                          v-if="
                            question?.displayType === 'images' &&
                              questionOptionForSegment(segment.id)?.imageUrl
                          "
                          :src="
                            getExternalUrl(
                              questionOptionForSegment(segment.id)?.imageUrl
                            )
                          "
                          :style="{
                            objectFit: 'cover',
                            width: '30px',
                            height: '30px',
                            borderRadius: '0.25rem',
                            flexShrink: 0
                          }"
                        >
                        <div
                          v-if="
                            question?.displayType !== 'images' ||
                              !questionOptionForSegment(segment.id)?.imageUrl
                          "
                          :style="{
                            border: '1px solid rgba(37, 99, 235, 0.4)',
                            width: '0.75rem',
                            height: '0.75rem',
                            borderRadius: '0.125rem',
                            flexShrink: 0
                          }"
                        />
                        <div :style="{ flex: 1, textAlign: 'left' }">
                          <div>{{ getSegmentName(segment.id) }}</div>
                          <div
                            v-if="getSegmentScore(segment.id)"
                            :style="{
                              fontSize: '0.75rem',
                              opacity: 0.6,
                              marginTop: '0.125rem'
                            }"
                          >
                            {{ getSegmentScore(segment.id) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </template>
              </div>
            </div>
          </template>
        </BaseNode>

        <!-- Render test variants dynamically -->
        <BaseNode
          v-for="(variant, index) in testVariants"
          :key="variant.id"
          :node="node"
          :icon="nodeIcon"
          :classes="'bg-amber-50 dark:bg-amber-950/30 ring-amber-300/50 dark:ring-amber-700/30'"
          :metrics="metrics"
          :menu-items="[]"
          :delete-label="'Remove this variant'"
          :context="`VARIANT #${index + 1}`"
          :variant-id="variant.id"
          :disable-drag="true"
          @delete-node="handleDeleteVariant(variant.id)"
        >
          <!-- Node type label -->
          <template #context>
            {{ contextText }}
          </template>

          <!-- Settings menu slot for webapp-specific actions -->
          <template #settings-menu>
            <slot name="settings-menu" />
          </template>

          <!-- Main content area -->
          <template v-if="node.ask">
            <template v-if="question">
              <div class="mb-1 mt-3 text-sm text-xs opacity-60">
                {{
                  segmentGroup?.name ||
                    (question.inputType
                      ? `Input: ${question.inputType}`
                      : 'Question')
                }}
              </div>
              <div
                class=""
                :style="getVariantQuestionStyles(variant)"
              >
                "{{ getVariantQuestionText(variant) }}"
              </div>
              <div
                v-if="hasTest && segmentGroup"
                class="-mx-2 my-2"
              >
                <!-- Answer options without progress bars for variants -->
                <div class="space-y-2">
                  <template
                    v-for="segment in sortedSegments"
                    :key="segment.id"
                  >
                    <div class="px-3 py-1 text-sm">
                      <div class="flex items-start leading-4">
                        <span class="mr-2">•</span>
                        <span
                          :style="getVariantAnswerStyles(variant, segment.id)"
                        >
                          {{ getVariantSegmentName(variant, segment.id) }}
                        </span>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </template>
            <template v-else>
              Select a question
            </template>

            <!-- Sync status indicators (only show open ended and scoring badges for variants) -->
            <div
              v-if="!templatePreview"
              class="mt-2.5 flex items-center gap-x-1"
            >
              <div
                v-if="segmentGroup?.isMultiWinning"
                class="flex items-center gap-x-1 rounded bg-purple-500/10 px-1.5 py-0.5"
                title="Multiple answers can be selected"
              >
                <div class="text-xs">
                  Multi Select
                </div>
                <component
                  :is="multiSelectIcon"
                  class="h-4 w-4"
                />
              </div>
              <div
                v-if="question?.otherOptions?.enabled"
                class="flex items-center gap-x-1 rounded px-1.5 py-0.5"
                :class="question?.otherOptions?.syncToCustomField ? 'bg-green-500/30' : 'bg-slate-500/10'"
                :title="question?.otherOptions?.syncToCustomField ? 'Open-ended responses synced to custom field' : 'Capturing open ended responses (not synced)'"
              >
                <div class="text-xs">
                  {{ question?.otherOptions?.syncToCustomField ? 'Other synced' : 'Open ended' }}
                </div>
                <component
                  :is="question?.otherOptions?.syncToCustomField ? loopIcon : openEndedIcon"
                  class="h-4 w-4"
                />
              </div>
              <div
                v-if="questionHasScoring"
                class="flex items-center gap-x-1 rounded bg-emerald-500/10 px-1.5 py-0.5"
                title="Answer options have scoring"
              >
                <div class="text-xs">
                  Scoring enabled
                </div>
                <component
                  :is="scoreIcon"
                  class="h-4 w-4"
                />
              </div>
            </div>
          </template>
          <template v-else>
            <template v-if="segmentGroup">
              <div>{{ segmentGroup.name }}</div>
            </template>
            <template v-else-if="node.type === 'random'">
              Randomly sending visitors to one of the following
              {{ node.split?.length || 0 }} groups
            </template>
            <template v-else>
              Select a segment group / question to route by
            </template>
          </template>

          <!-- Hover tooltip with segments (with or without audience breakdown) -->
          <template
            v-if="node.ask && segmentGroup && !hasTest"
            #hover
          >
            <div
              :style="{
                display: 'flex',
                maxWidth: '300px',
                flexDirection: 'column',
                gap: '1rem',
                padding: '1rem 0.5rem'
              }"
            >
              <div
                :style="{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }"
              >
                <div
                  v-if="scale < 1"
                  class="mb-3 text-base font-bold"
                >
                  {{ questionText }}
                </div>

                <!-- With audience breakdown -->
                <template v-if="audienceBreakdown">
                  <template
                    v-for="segment in sortedSegments"
                    :key="segment.id"
                  >
                    <div :style="{ position: 'relative' }">
                      <div
                        class="segment-bar-hover"
                        :style="{
                          position: 'relative',
                          '--segment-width': audienceBreakdown.total
                            ? `${((segment.count / audienceBreakdown.total) * 100).toFixed(0)}%`
                            : '0%'
                        }"
                      >
                        <div
                          :style="{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '1.5rem',
                            padding: '0.25rem 0 0.25rem 0.5rem',
                            fontSize: '0.875rem',
                            color: 'inherit'
                          }"
                        >
                          <div
                            :style="{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              flex: 1
                            }"
                          >
                            <img
                              v-if="
                                question?.displayType === 'images' &&
                                  questionOptionForSegment(segment.id)?.imageUrl
                              "
                              :src="
                                getExternalUrl(
                                  questionOptionForSegment(segment.id)?.imageUrl
                                )
                              "
                              :style="{
                                objectFit: 'cover',
                                width: '30px',
                                height: '30px',
                                borderRadius: '0.25rem',
                                flexShrink: 0
                              }"
                            >
                            <div
                              :style="{
                                lineHeight: '1rem',
                                textAlign: 'left'
                              }"
                            >
                              <div>{{ getSegmentName(segment.id) }}</div>
                              <div
                                v-if="getSegmentScore(segment.id)"
                                :style="{
                                  fontSize: '0.75rem',
                                  opacity: 0.6,
                                  marginTop: '0.125rem'
                                }"
                              >
                                {{ getSegmentScore(segment.id) }}
                              </div>
                            </div>
                          </div>
                          <div
                            v-if="!templatePreview"
                            :style="{
                              display: 'flex',
                              gap: '0.25rem'
                            }"
                          >
                            <div>
                              {{
                                new Intl.NumberFormat().format(segment.count)
                              }}
                            </div>
                            <div :style="{ opacity: 0.3 }">
                              /
                            </div>
                            <div :style="{ fontWeight: 'bold' }">
                              {{
                                audienceBreakdown.total
                                  ? (
                                    (segment.count /
                                      audienceBreakdown.total) *
                                    100
                                  ).toFixed(1)
                                  : '0.0'
                              }}%
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </template>

                <!-- Without audience breakdown - just show segment names -->
                <template v-else>
                  <template
                    v-for="segment in segmentGroup.segments"
                    :key="segment.id"
                  >
                    <div
                      :style="{
                        padding: '0.25rem 0',
                        fontSize: '0.875rem',
                        color: 'inherit'
                      }"
                    >
                      <div
                        :style="{
                          padding: '0 0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }"
                      >
                        <img
                          v-if="
                            question?.displayType === 'images' &&
                              questionOptionForSegment(segment.id)?.imageUrl
                          "
                          :src="
                            getExternalUrl(
                              questionOptionForSegment(segment.id)?.imageUrl
                            )
                          "
                          :style="{
                            objectFit: 'cover',
                            width: '30px',
                            height: '30px',
                            borderRadius: '0.25rem',
                            flexShrink: 0
                          }"
                        >
                        <div
                          v-if="
                            question?.displayType !== 'images' ||
                              !questionOptionForSegment(segment.id)?.imageUrl
                          "
                          :style="{
                            border: '1px solid rgba(37, 99, 235, 0.4)',
                            width: '0.75rem',
                            height: '0.75rem',
                            borderRadius: '0.125rem',
                            flexShrink: 0
                          }"
                        />
                        <div :style="{ flex: 1, textAlign: 'left' }">
                          <div>{{ getSegmentName(segment.id) }}</div>
                          <div
                            v-if="getSegmentScore(segment.id)"
                            :style="{
                              fontSize: '0.75rem',
                              opacity: 0.6,
                              marginTop: '0.125rem'
                            }"
                          >
                            {{ getSegmentScore(segment.id) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </template>
              </div>
            </div>
          </template>
        </BaseNode>

        <div
          v-if="hasTest && testIsDraft"
          class="flowbuilder-tests-placeholder"
        >
          <div
            class="flowbuilder-test-placeholder"
            @click="handleAddVariant"
          >
            <div class="flowbuilder-test-placeholder-text">
              Add new variant
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Split branches -->
    <!-- Hide split branches for multi-select questions as routing is not supported -->
    <div
      v-if="node.split && node.split.length > 0 && !segmentGroup?.isMultiWinning"
      class="flowbuilder-splits flowbuilder-splits-linear"
      @click.stop
    >
      <div
        v-for="(splitPath, splitIndex) in filteredSplits"
        :key="getSplitKey(splitPath.path, splitPath.originalIndex)"
        class="flowbuilder-split"
      >
        <!-- Branch label -->
        <div class="flowbuilder-split-label">
          <template v-if="node.type === 'random'">
            Group {{ splitIndex + 1 }}
          </template>
          <template v-else>
            {{ getSplitLabel(splitPath.path.segmentId) }}
          </template>
          <div
            v-if="useEnhancedReporting"
            class="flowbuilder-split-user-count"
          >
            <UsersIcon class="h-2.5 w-2.5" />
            <span class="user-count-amount">
              <template v-if="node.type === 'random'">
                {{
                  getSplitVisitorCount(String(splitIndex + 1)) >= 0
                    ? getSplitVisitorCount(String(splitIndex + 1))
                    : 0
                }}
              </template>
              <template v-else>
                {{
                  getSplitVisitorCount(splitPath.path.segmentId) >= 0
                    ? getSplitVisitorCount(splitPath.path.segmentId)
                    : 0
                }}
              </template>
            </span>
          </div>
        </div>

        <!-- Recursively render child nodes -->
        <NodeRun
          :nodes="splitPath.path.child"
          :show-final-dropzone="true"
          :parent-node="splitPath.path"
          :in-redirect-funnel="inRedirectFunnel"
          @nodes-updated="handleNodesUpdated(splitPath.originalIndex, $event)"
          @node-action="
            (...args) => handleNodeAction(splitPath.originalIndex, ...args)
          "
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * SplitNode Component
 *
 * Handles branching logic in flows. Supports three types of splits:
 * 1. **Question splits** - Route based on user answers to questions
 * 2. **Segment splits** - Route based on predefined segments
 * 3. **Random splits** - Randomly distribute visitors across branches
 *
 * Key features:
 * - Visual representation of branches with labels
 * - Audience breakdown tooltips showing distribution
 * - Sync status indicators for integrated systems
 * - Support for open-ended responses
 *
 * The component is designed to work with different data sources
 * through props, making it reusable across different contexts.
 */

import { computed, inject, ref } from 'vue'
import BaseNode from './BaseNode.vue'
import NodeRun from './NodeRun.vue'
import { stripHtml, renderText, segmentName } from '@rightmessage/shared-utils'
import { getNodeIcon } from '../../utils/nodeIcons'
import { resolveFlowIcon } from '../icons'
import { isQuestionAskedBeforeNode } from '../../services/flowUtilities'
import type { SplitNodeType, Question, SegmentGroup, Phrase } from '../../types'
import { PlayIcon, BeakerIcon, ListBulletIcon } from '@heroicons/vue/24/outline'

// Icons - passed as props for flexibility
interface Props {
  node: SplitNodeType
  icon?: any
  segmentGroups?: SegmentGroup[]
  questions?: Question[]
  syncStatus?: 'all_values' | 'some_values' | 'none'
  showSyncStatus?: boolean
  LoopIcon?: any
  OpenEndedIcon?: any
  ChartBarIcon?: any
}

const props = withDefaults(defineProps<Props>(), {
  showSyncStatus: true
})

const emit = defineEmits<{
  edit: []
  'delete-node': []
  'add-after': []
}>()

// A/B test state - will be populated from parent
const activeTest = inject<any>('getNodeTest', () => null)
const splitTestData = computed(() => {
  if (!props.node.segmentGroupId) return null
  return activeTest(props.node)
})

const hasTest = computed(() => {
  // Don't show test UI if node.ask is false
  if (!props.node.ask) return false
  if (!splitTestData.value) return false
  // Show test UI if test exists and is not completed
  return splitTestData.value.status !== 'completed'
})

// Test status computed properties
const testIsDraft = computed(() => {
  return splitTestData.value?.status === 'draft'
})

const testIsActive = computed(() => {
  return splitTestData.value?.status === 'active'
})

const testStatusClasses = computed(() => {
  if (testIsActive.value) {
    return 'flowbuilder-tests-status-active'
  }
  return 'flowbuilder-tests-status-draft'
})

const testStatusIcon = computed(() => {
  if (testIsActive.value) {
    return PlayIcon
  }
  return BeakerIcon
})

const testStatusLabel = computed(() => {
  if (testIsActive.value) {
    return 'Test Running - '
  }
  return 'Test Draft - '
})

const testVariants = computed(() => {
  if (!splitTestData.value || !hasTest.value) return []
  return splitTestData.value.variants || []
})

// Inject adapter for handling node actions
const adapter = inject('adapter') as any

// Inject confirm dialog
const confirm = inject('confirm') as any

// Inject preview modes
const templatePreview = inject('templatePreview', false)

// Inject inRedirectFunnel to pass down to child NodeRun components
const inRedirectFunnel = inject('inRedirectFunnel', false)

// Inject data from parent if not provided as props
const injectedSegmentGroups = inject('segmentGroups', ref([]))
const injectedQuestions = inject('questions', ref([]))
const injectedPhrases = inject('phrases', ref([]))
const nodePropsData = inject('nodeProps', ref({}))

// Inject flow and widgets to check widget settings
const flow = inject('flow', ref(null))
const widgets = inject('widgets', ref([]))

// Get variant diff utilities from adapter if available
const getVariantDiff = adapter?.getVariantDiff || null
const hasAnswerChanged = adapter?.hasAnswerChanged || null
const getVariantChangeStyles = adapter?.getVariantChangeStyles || null
const getAudienceBreakdown = inject<(segmentGroupId: string) => any>(
  'getAudienceBreakdown',
  () => null
)
const getNodeMetrics = inject<(node: any) => any>('getNodeMetrics', () => null)
// Inject getRawNodeMetrics function
const getRawNodeMetrics = inject<(node: any) => any>(
  'getRawNodeMetrics',
  () => null
)
const getNodeSyncStatus = inject<(node: any) => any>(
  'getNodeSyncStatus',
  () => null
)
const scale = inject(
  'scale',
  computed(() => 1)
)

// Use props data if provided, otherwise fall back to injected data
const segmentGroups = computed(
  () => props.segmentGroups || injectedSegmentGroups.value
)
const questions = computed(() => props.questions || injectedQuestions.value)

// Get the appropriate icon for this node
const nodeIcon = computed(() => {
  // Fall back to iconify icons
  return getNodeIcon(props.node)
})

// Get icon components from props or nodeProps
const loopIcon = computed(() => {
  return resolveFlowIcon(
    props.LoopIcon || nodePropsData.value.icons?.LoopIcon || 'hugeicons:refresh'
  )
})

const openEndedIcon = computed(() => {
  return resolveFlowIcon(
    props.OpenEndedIcon ||
    nodePropsData.value.icons?.OpenEndedIcon ||
    'hugeicons:message-bubble'
  )
})

const scoreIcon = computed(() => {
  return resolveFlowIcon(
    props.ChartBarIcon ||
    nodePropsData.value.icons?.ChartBarIcon ||
    'hugeicons:chart-bar'
  )
})

const multiSelectIcon = computed(() => {
  return (
    nodePropsData.value.icons?.MultiSelectIcon ||
    ListBulletIcon
  )
})

// Compute segment group and question from data
const segmentGroup = computed(() => {
  if (!props.node.segmentGroupId) return null
  // Check if it's a dimension ID
  if (props.node.segmentGroupId.startsWith('dim_')) {
    return segmentGroups.value.find((sg) => sg.id === props.node.segmentGroupId)
  }
  return null
})

const question = computed(() => {
  if (!props.node.segmentGroupId) return null
  // For input questions (raq_*), find by ID directly
  if (props.node.segmentGroupId.startsWith('raq_')) {
    return questions.value.find((q) => q.id === props.node.segmentGroupId)
  }
  // For regular questions linked to dimensions
  return questions.value.find(
    (q) =>
      q.segmentGroupId === props.node.segmentGroupId ||
      q.dimensionId === props.node.segmentGroupId
  )
})

const questionOptionForSegment = (segmentId: string) => {
  if (!question.value) return null
  return question.value.options.find((o) => o.segmentId === segmentId)
}

// Check if question has scoring enabled on any options
const questionHasScoring = computed(() => {
  if (!question.value || !question.value.options) return false
  return question.value.options.some(
    (option) =>
      option.scores && option.scores.some((s: any) => s.score && s.amount !== 0)
  )
})

const getExternalUrl = computed(() => {
  return nodePropsData.value.getExternalUrl || ((url: string) => url)
})

/**
 * Dynamic node styling based on type
 * - Questions: Amber/yellow theme
 * - Segments/Random: Fuchsia theme
 */
const nodeBackground = computed(() => {
  if (props.node.ask) {
    return 'bg-amber-100 ring-amber-400/50 dark:bg-yellow-950 dark:ring-yellow-700/50'
  }
  return 'bg-fuchsia-100 ring-fuchsia-400/50 dark:bg-fuchsia-900 dark:ring-fuchsia-500/50'
})

/**
 * Context text that appears at the top of the node
 * Provides clear indication of what the node does
 */
const contextText = computed(() => {
  if (props.node.ask) {
    if (
      question.value?.options &&
      props.node.segmentGroupId?.startsWith('dim')
    ) {
      const count = question.value.options.length
      return `Ask a question (${count} possible answer${count === 1 ? '' : 's'}):`
    }
    return 'Ask a question:'
  }
  if (props.node.type === 'random') {
    return 'Random Split:'
  }
  return 'Route by:'
})

const inputLabel = computed(() => {
  switch (question.value.inputType) {
    case 'text':
      return 'Text Input'
    case 'textarea':
      return 'Longer Text'
    case 'number':
      return 'Number'
    case 'scale':
      return 'Rating Scale'
  }
})

// Question display text
const questionText = computed(() => {
  if (!question.value) return ''
  return stripHtml(
    renderText(question.value.text || '', { phrases: injectedPhrases.value })
  )
})

const answerText = (text) => {
  return stripHtml(renderText(text, { phrases: injectedPhrases.value }))
}

// Get flow-specific segment breakdown from metrics
const getFlowSegmentBreakdown = () => {
  // First try to get raw metrics directly for more accurate data
  const rawMetrics = getRawNodeMetrics ? getRawNodeMetrics(props.node) : null

  // For all questions (with or without routing), check for answer distribution
  if (rawMetrics && segmentGroup.value) {
    // Check for either answerDistribution (all questions track this) or splitDistribution (questions with routing)
    const distribution =
      rawMetrics.answerDistribution || rawMetrics.splitDistribution

    if (distribution) {
      const segments = []
      let total = 0

      segmentGroup.value.segments.forEach((segment) => {
        const count = distribution[segment.id] || 0
        total += count
        segments.push({ id: segment.id, count })
      })

      // Add fallback/default if exists
      if (distribution['fallback']) {
        total += distribution['fallback']
        segments.push({ id: null, count: distribution['fallback'] })
      }

      return { segments, total }
    }
  }

  // Fallback to formatted metrics if raw metrics not available
  const metrics = getNodeMetrics(props.node)
  if (metrics) {
    const splits = metrics.find((o) => o.label === 'Splits')?.value

    if (splits) {
      try {
        const splitData = JSON.parse(splits)
        const segments = []
        let total = 0

        // Build segments array from flow metrics
        if (segmentGroup.value) {
          segmentGroup.value.segments.forEach((segment) => {
            const count = splitData[segment.id] || 0
            total += count
            segments.push({ id: segment.id, count })
          })

          // Add fallback/default if exists
          if (splitData['fallback']) {
            total += splitData['fallback']
            segments.push({ id: null, count: splitData['fallback'] })
          }
        }

        return { segments, total }
      } catch (e) {
        console.error('Error parsing splits data:', e)
      }
    }
  }

  // If we still don't have any data but have segments, return an empty structure so tooltip still shows
  if (segmentGroup.value && props.node.ask) {
    const segments = segmentGroup.value.segments.map((segment) => ({
      id: segment.id,
      count: 0
    }))

    return {
      segments,
      total: 0
    }
  }

  return null
}

// Audience breakdown - prioritize flow metrics, fall back to account-wide
const audienceBreakdown = computed(() => {
  // When enhanced reporting is enabled, only use flow-specific metrics
  if (useEnhancedReporting.value) {
    return getFlowSegmentBreakdown()
  }

  // For legacy customers without enhanced reporting, show account-wide breakdown
  if (props.node.segmentGroupId) {
    return getAudienceBreakdown(props.node.segmentGroupId)
  }
  return null
})

// Sorted segments for audience breakdown
const sortedSegments = computed(() => {
  if (!audienceBreakdown.value) return []

  // When showing test variants, maintain original order
  if (hasTest.value) {
    return audienceBreakdown.value.segments
  }

  // Only sort by count when not in a test
  return [...audienceBreakdown.value.segments].sort((a, b) => b.count - a.count)
})

// Inject function to get split test metrics
const getSplitTestMetrics = inject<(testPid: string) => any>(
  'getSplitTestMetrics',
  () => null
)

// Check if enhanced reporting is enabled via adapter's global settings or injected settings
const settings = inject<any>('settings', ref({}))
const useEnhancedReporting = computed(() => {
  // First check adapter's globalSettings if available
  if (adapter?.globalSettings?.value?.enhancedFlowReporting !== undefined) {
    return adapter.globalSettings.value.enhancedFlowReporting === true
  }
  // Fall back to injected settings
  return settings.value?.enhancedFlowReporting === true
})

// Check if widgets are configured to ask questions (not skipping)
const widgetsAskQuestions = computed(() => {
  if (flow.value?.id && widgets.value && widgets.value.length > 0) {
    const associatedWidgets = widgets.value.filter(
      (widget: any) => widget.contentId === flow.value.id
    )
    return associatedWidgets.some(
      (widget: any) => widget.whenToAskNext !== 'none'
    )
  }
  return false
})

// Check if the Default path should be hidden in the UI
const shouldHideDefaultInUI = computed(() => {
  if (!props.node.split || props.node.split.length === 0) return false

  // Case 1: Question node with routing (ask + split) — existing behavior
  if (props.node.ask) {
    return widgetsAskQuestions.value
  }

  // Case 2: Router-only node (no ask) where the same question is asked
  // earlier in the flow path — Default is unreachable
  if (
    !props.node.ask &&
    props.node.segmentGroupId &&
    props.node._uuid &&
    flow.value?.funnelDefinition
  ) {
    return (
      widgetsAskQuestions.value &&
      isQuestionAskedBeforeNode(
        flow.value.funnelDefinition,
        props.node._uuid,
        props.node.segmentGroupId
      )
    )
  }

  return false
})

// Filter splits to exclude Default when questions are skipped
const filteredSplits = computed(() => {
  if (!props.node.split || props.node.split.length === 0) return []

  return props.node.split
    .map((splitPath, originalIndex) => ({ path: splitPath, originalIndex }))
    .filter(
      ({ path }) => !(shouldHideDefaultInUI.value && path.segmentId === null)
    )
})

// Get visitor count for a specific split
const getSplitVisitorCount = (segmentId: string | null) => {
  const m = getNodeMetrics(props.node)
  if (!m) return -1

  const splits = m.find((o) => o.label === 'Splits')?.value
  if (!splits) return -1

  try {
    const obj = JSON.parse(splits)

    if (!segmentId) {
      return obj['fallback'] || 0
    }

    return obj[segmentId] || 0
  } catch (e) {
    return 0
  }
}

// Metrics from webapp
const metrics = computed(() => {
  // If there's an active test, don't show metrics on the control/variant nodes
  if (hasTest.value) {
    return null
  }

  // If we're in routing mode (ask=false) with splits, don't show metrics in BaseNode
  // They should appear as badges under each branch instead
  if (!props.node.ask && props.node.split && props.node.split.length > 0) {
    return null
  }

  // If node is set to ask but no question is selected, don't show metrics
  if (props.node.ask && !props.node.segmentGroupId) {
    return null
  }

  // Get the raw metrics
  const rawMetrics = getNodeMetrics(props.node)
  if (!rawMetrics) return null

  // For questions with splits (ask=true and split=true), filter out the "Splits" metric
  // This ensures they display like regular questions (Asks, Completions, Answer Rate)
  if (props.node.ask && props.node.split && props.node.split.length > 0) {
    return rawMetrics.filter((metric) => metric.label !== 'Splits')
  }

  // No test active and not in routing mode, use regular metrics
  return rawMetrics
})

const sanitizedMetrics = computed(() => {
  if (!metrics.value) return null

  return metrics.value.filter((o) => o.label !== 'Splits')
})

// Check if there's a default split (null segmentId)
const hasDefaultSplit = computed(() => {
  return props.node.split?.some((split) => split.segmentId === null) || false
})

// Helper functions
const getSegmentName = (segmentId: string) => {
  if (!segmentGroup.value) return ''
  const segment = segmentGroup.value.segments.find((s) => s.id === segmentId)
  if (!segment) return 'Default'

  const name = segmentName(segment, {
    segmentGroups: segmentGroups.value,
    questions: questions.value
  })

  return stripHtml(renderText(name || ''))
}

// Get variant-specific question text
const getVariantQuestionText = (variant: any) => {
  // Use variant's text if available, otherwise fall back to default
  if (variant.text) {
    return stripHtml(
      renderText(variant.text, { phrases: injectedPhrases.value })
    )
  }
  return questionText.value
}

// Get variant-specific segment name/label
const getVariantSegmentName = (variant: any, segmentId: string) => {
  // Check if variant has custom answer labels
  if (variant.answers && variant.answers[segmentId]) {
    const answer = variant.answers[segmentId]
    // Handle both string (legacy) and object format
    const label = typeof answer === 'string' ? answer : answer.label
    if (label) {
      // Create a pseudo-segment object to pass to segmentName utility
      const pseudoSegment = { id: segmentId, name: label }
      const processedName = segmentName(pseudoSegment, {
        segmentGroups: segmentGroups.value,
        questions: questions.value
      })
      return stripHtml(renderText(processedName || label))
    }
  }
  // Fall back to default segment name
  return getSegmentName(segmentId)
}

// Get styles for variant question text
const getVariantQuestionStyles = (variant: any) => {
  if (!getVariantDiff || !getVariantChangeStyles || !question.value) return {}

  const diff = getVariantDiff(question.value, variant)
  return getVariantChangeStyles(diff.hasQuestionTextChanged)
}

// Get styles for variant answer text
const getVariantAnswerStyles = (variant: any, segmentId: string) => {
  if (
    !getVariantDiff ||
    !hasAnswerChanged ||
    !getVariantChangeStyles ||
    !question.value
  )
    return {}

  const diff = getVariantDiff(question.value, variant)
  return getVariantChangeStyles(hasAnswerChanged(segmentId, diff))
}

const getSplitLabel = (segmentId: string | null) => {
  if (props.node.type === 'sg') {
    if (!segmentId) return 'Default'
    return getSegmentName(segmentId)
  }
  if (props.node.type === 'random') {
    return 'Random'
  }
  return ''
}

const getSplitKey = (splitPath: any, splitIndex: number) => {
  if (props.node.type === 'sg') return splitPath.segmentId || 'default'
  if (props.node.type === 'random') return `random-${splitIndex}`
  return splitIndex
}

// Get score information for a segment
const getSegmentScore = (segmentId: string) => {
  if (!question.value || !question.value.options) return null

  const option = question.value.options.find(
    (opt) => opt.segmentId === segmentId
  )
  if (!option || !option.scores || option.scores.length === 0) return null

  const activeScores = option.scores.filter((s: any) => s.score && s.amount !== 0)
  if (activeScores.length === 0) return null

  return activeScores
    .map((s: any) => {
      const sign = s.amount > 0 ? '+' : ''
      return `${s.score} ${sign}${s.amount}`
    })
    .join(', ')
}

// Get sync status from injected function or props
const syncStatus = computed(() => {
  // Use prop if provided
  if (props.syncStatus) return props.syncStatus

  // Otherwise, use the injected function
  if (getNodeSyncStatus && props.node.ask && props.node.segmentGroupId) {
    const status = getNodeSyncStatus(props.node)
    //console.log('SplitNode sync status:', status, 'for node:', props.node)
    return status
  }

  return null
})

// Sync status styling
const syncStatusClasses = computed(() => {
  const status = syncStatus.value
  if (status === 'all_values') return 'bg-green-500/30'
  if (status === 'some_values') return 'bg-orange-500/20'
  return 'bg-red-500/20'
})

const syncStatusText = computed(() => {
  const status = syncStatus.value
  if (status === 'all_values') return 'All answers synced'
  if (status === 'some_values') return 'Some answers synced'
  return 'Answers not synced'
})

const syncStatusTitle = computed(() => syncStatusText.value)

// Menu items for this node type
const menuItems = computed(() => {
  const items = []

  // Add routing toggle for question nodes that have a question selected
  if (props.node.ask && props.node.segmentGroupId) {
    // Only show "Test this question" if no test exists
    if (!hasTest.value) {
      items.push({
        label: 'Test this question',
        icon: nodePropsData.value.icons?.PresentationChartLineIcon,
        command: () => {
          handleCreateTest()
        }
      })
    } else {
      // Show option to end test
      // items.push({
      //   label: 'End test',
      //   icon: 'hugeicons:stop',
      //   command: () => {
      //     handleEndTest()
      //   }
      // })
    }

    // Only show routing options for single-select questions
    // Multi-select questions cannot have routing
    if (!segmentGroup.value?.isMultiWinning) {
      if (!props.node.split || props.node.split.length === 0) {
        items.push({
          label: 'Route off this question',
          icon: nodePropsData.value.icons?.FunnelIcon,
          command: () => {
            if (!segmentGroup.value) return

            props.node.split = [
              ...segmentGroup.value.segments.map((s) => ({
                segmentId: s.id,
                child: []
              })),
              {
                segmentId: null,
                child: []
              }
            ]
          }
        })
      } else {
        items.push({
          label: 'Remove routing from this question',
          icon: nodePropsData.value.icons?.FunnelIcon,
          command: () => {
            props.node.split = false
          }
        })
      }
    }
    // items.push({
    //   label: 'Archive this question',
    //   icon: nodePropsData.value.icons?.FunnelIcon,
    //   command: () => {
    //     alert('really delete this?')
    //   }
    // })
  }

  return items
})

// Handle test creation
const handleCreateTest = async () => {
  console.log('handleCreateTest called', {
    adapter,
    hasAdapter: !!adapter,
    hasOnCreateTest: !!adapter?.onCreateTest,
    segmentGroupId: props.node.segmentGroupId,
    questionText: questionText.value
  })

  // Notify adapter if available
  if (adapter?.onCreateTest) {
    await adapter.onCreateTest({
      resourceType: 'question',
      resourceId: props.node.segmentGroupId, // This will be the dim_* ID
      segmentGroupId: props.node.segmentGroupId, // Pass it explicitly so adapter can look up question
      questionText: questionText.value
    })
  } else {
    console.error('No adapter or onCreateTest method available')
  }
}

// Handle ending test
const handleEndTest = async () => {
  if (adapter?.onEndTest && splitTestData.value) {
    await adapter.onEndTest(splitTestData.value.pid)
  }
}

// Handle viewing test results
const handleViewResults = () => {
  if (adapter?.onViewTestResults && splitTestData.value) {
    adapter.onViewTestResults(splitTestData.value.pid)
  }
}

// Handle starting test
const handleStartTest = () => {
  console.log('handleStartTest called', {
    confirm: !!confirm,
    adapter: !!adapter,
    hasOnStartTest: !!adapter?.onStartTest,
    splitTestData: splitTestData.value
  })

  if (!confirm) {
    console.error('No confirm dialog available')
    // Fallback: use adapter's onStartTest directly if no confirm available
    if (adapter?.onStartTest && splitTestData.value) {
      if (
        window.confirm(
          "Once you start this test, you won't be able to make any edits while the test is running. Are you sure you want to start this test?"
        )
      ) {
        adapter.onStartTest(splitTestData.value.pid)
      }
    }
    return
  }

  if (!adapter?.onStartTest || !splitTestData.value) {
    console.error('Missing adapter.onStartTest or splitTestData', {
      hasOnStartTest: !!adapter?.onStartTest,
      splitTestData: splitTestData.value
    })
    return
  }

  confirm.require({
    message:
      "Once you start this test, you won't be able to make any edits while the test is running. Are you sure you want to start this test?",
    header: 'Start A/B Test?',
    acceptProps: { label: 'Start Test', severity: 'info' },
    rejectProps: { label: 'Cancel', outlined: true },
    accept: async () => {
      await adapter.onStartTest(splitTestData.value.pid)
    }
  })
}

// Handle adding a new variant
const handleAddVariant = async () => {
  if (adapter?.onAddTestVariant && splitTestData.value) {
    // Create answers object with current segment names
    const answers = {}
    if (segmentGroup.value) {
      segmentGroup.value.segments.forEach((segment) => {
        // Use getSegmentName to properly handle INT_OTHER labels
        const displayName = getSegmentName(segment.id)
        answers[segment.id] = {
          label: displayName,
          imageUrl: questionOptionForSegment(segment.id)?.imageUrl || ''
        }
      })
    }

    const newVariant = {
      name: `Variant ${testVariants.value.length + 1}`,
      text: questionText.value,
      answers: answers
    }
    await adapter.onAddTestVariant(splitTestData.value.pid, newVariant)
  }
}

// Handle deleting a variant
const handleDeleteVariant = async (variantId: string) => {
  if (adapter?.onDeleteTestVariant && splitTestData.value) {
    await adapter.onDeleteTestVariant(splitTestData.value.pid, variantId)
  }
}

// Handle canceling test
const handleCancelTest = () => {
  console.log('handleCancelTest called', {
    confirm: !!confirm,
    adapter: !!adapter,
    hasOnDeleteTest: !!adapter?.onDeleteTest,
    splitTestData: splitTestData.value
  })

  if (!confirm) {
    console.error('No confirm dialog available')
    // Fallback: use native confirm if no confirm available
    if (adapter?.onDeleteTest && splitTestData.value) {
      if (
        window.confirm(
          'This will remove all variants and revert back to the content currently in the control. Are you sure you want to cancel this test?'
        )
      ) {
        adapter.onDeleteTest(splitTestData.value.pid)
      }
    }
    return
  }

  if (!adapter?.onDeleteTest || !splitTestData.value) {
    console.error('Missing adapter.onDeleteTest or splitTestData', {
      hasOnDeleteTest: !!adapter?.onDeleteTest,
      splitTestData: splitTestData.value
    })
    return
  }

  confirm.require({
    message:
      'This will remove all variants and revert back to the content currently in the control. Are you sure you want to cancel this test?',
    header: 'Cancel A/B Test?',
    acceptProps: { label: 'Cancel Test', severity: 'danger' },
    rejectProps: { label: 'Keep Test', outlined: true },
    accept: async () => {
      await adapter.onDeleteTest(splitTestData.value.pid)
    }
  })
}

// Handle cloning a variant
const handleCloneVariant = async (variantId: string) => {
  if (adapter?.onAddTestVariant && splitTestData.value) {
    // Find the variant to clone
    const variantToClone = testVariants.value.find((v) => v.id === variantId)
    if (!variantToClone) return

    // Create a deep clone of the variant
    const clonedVariant = {
      name: `${variantToClone.name} (Copy)`,
      text: variantToClone.text || questionText.value,
      answers: JSON.parse(JSON.stringify(variantToClone.answers || {}))
    }

    await adapter.onAddTestVariant(splitTestData.value.pid, clonedVariant)
  }
}

// Menu items for variants
const variantMenuItems = (variantId: string) => {
  return computed(() => {
    const items = []

    // Clone variant option
    items.push({
      label: 'Clone variant',
      icon: 'hugeicons:copy',
      command: () => {
        handleCloneVariant(variantId)
      }
    })

    // Only show delete if there's more than one variant
    if (testVariants.value.length > 1) {
      items.push({
        separator: true
      })
      items.push({
        label: 'Delete variant',
        icon: 'hugeicons:trash',
        class: 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20',
        command: () => {
          handleDeleteVariant(variantId)
        }
      })
    }

    console.log('variantMenuItems', items)

    return items
  })
}

// Handle nodes update from child NodeRun
const handleNodesUpdated = (splitIndex: number, newNodes: any[]) => {
  if (props.node.split && props.node.split[splitIndex]) {
    props.node.split[splitIndex].child = newNodes
  }
}

// Handle node actions from child NodeRun
const handleNodeAction = (
  splitIndex: number,
  nodeIndex: number,
  action: string,
  data?: any
) => {
  const splitPath = props.node.split?.[splitIndex]
  if (!splitPath) return

  const targetNode = splitPath.child[nodeIndex]
  if (!targetNode) return

  if (action === 'click' && adapter?.onNodeClick) {
    adapter.onNodeClick(targetNode)
  } else if (action === 'delete' && adapter?.onNodeDelete) {
    adapter.onNodeDelete(targetNode)
  } else if (action === 'add-routing' && adapter?.onAddRouting) {
    adapter.onAddRouting(targetNode)
  } else if (action === 'remove-routing' && adapter?.onRemoveRouting) {
    adapter.onRemoveRouting(targetNode)
  }
}

// Check if there are any child nodes in any split paths
const hasChildNodes = computed(() => {
  if (!props.node.split || props.node.split.length === 0) return false
  return props.node.split.some(splitPath => splitPath.child && splitPath.child.length > 0)
})

// Handle delete with confirmation if there are routing paths with child nodes
const handleDelete = () => {
  if (!hasChildNodes.value) {
    emit('delete-node')
    return
  }

  if (!confirm) {
    // Fallback to native confirm if PrimeVue confirm not available
    if (window.confirm('This will remove all nodes in each routing path. The actual questions, forms, and offers will not be deleted. Are you sure?')) {
      emit('delete-node')
    }
    return
  }

  confirm.require({
    header: 'Remove?',
    message: 'This will remove all nodes in each routing path. The actual questions, forms, and offers will not be deleted.',
    acceptProps: { label: 'Remove', severity: 'danger' },
    rejectProps: { label: 'Cancel', outlined: true },
    accept: () => {
      emit('delete-node')
    }
  })
}
</script>

<style scoped>
/* Dynamic segment bar width using CSS custom property */
.segment-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  height: 100%;
  width: var(--segment-width);
  border-bottom: 1px solid rgb(59 130 246 / 0.2);
  background-color: rgb(59 130 246 / 0.1);
}

.dark .segment-bar::after {
  background-color: rgb(100 116 139 / 0.2);
}

/* Inline segment bar for test visualization */
.segment-bar-inline::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  height: 100%;
  width: var(--segment-width);
  border-bottom: 1px solid rgb(59 130 246 / 0.2);
  background-color: rgb(59 130 246 / 0.1);
}

.dark .segment-bar-inline::after {
  background-color: rgb(100 116 139 / 0.2);
}
</style>

<style>
/* Non-scoped styles for teleported hover content */
.segment-bar-hover::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  height: 100%;
  width: var(--segment-width);
  border-bottom: 1px solid rgb(59 130 246 / 0.2);
  background-color: rgb(59 130 246 / 0.1);
}

@media (prefers-color-scheme: dark) {
  .segment-bar-hover::after {
    background-color: rgb(100 116 139 / 0.2);
  }
}
</style>
